import React, { useState, useMemo, useEffect, useRef, useCallback, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../../../components/Layout';
import { PatientDataContext } from '../../../context/PatientDataContext';
import './styles/ReviewDocuments.css';
import { db, auth } from '../../../firebase';
import { TriggerRenderPatientReport } from '../../../utils/TriggerRenderPatientReport';
import { getRenderCfName } from '../../../utils/autoPatientReport';
import { InitialWarningDialog } from './components/InitialWarningDialog';
import { doc, runTransaction, serverTimestamp } from 'firebase/firestore';
import WarningBar from './components/WarningBar';
import DocumentReviewHeader from './components/DocumentReviewHeader';
import { decodeHtmlEntities } from './functions/htmlEncoders';
import { handlePlainTextPaste, suppressFormattingShortcuts } from './functions/contentEditableUtils';
import { checkReportLogicVersion } from '../../../utils/featureFlagUtils';
import { determinePatientReportType } from '../../../utils/reportTypeUtils';
import { useAuth } from '../../../hooks/useAuth';
import { useFloatingActionPanel } from './components/floatingPanel/useFloatingActionPanel';
import { FloatingActionPanel } from './components/floatingPanel/FloatingActionPanel';
import { buildFirstApprovedFieldsDotNotation, appendReportAuditEntry } from '../../../utils/reportAuditHelpers';
import { useApprovalTracking } from '../../../hooks/useApprovalTracking';
import { renderModelInput as _renderModelInput } from '../../../utils/renderModelInput';
import {
  emitParagraphApproval,
  emitParagraphEdited,
  emitParagraphReset,
} from '../../../analytics/reportEvents';

// Strips the legacy "N. " number prefix from strength titles stored in
// Firestore (e.g. "1. Hyperfocus" -> "Hyperfocus"). Existing patients may
// have numbered titles; new patients will already be unnumbered.
const stripStrengthPrefix = (title) => {
  if (!title) return null;
  return title.includes('. ') ? title.split('. ')[1] : title;
};

export default function ReviewDocumentPR() {

  const { patientId } = useParams();
  const { claims } = useAuth();

  const MODULE_NUMBER = 6
  const MODULE_PREFIX_CODE = "pr"
  const MODULE_TITLE = "Patient Report"

  const navigate = useNavigate();
  const { patientDataModel, fetchPatientData, clearPatientData } = useContext(PatientDataContext);


  // State for feature flag
  const [reportLogicVersion, setReportLogicVersion] = useState(1);
  const [isLoadingFeatureFlag, setIsLoadingFeatureFlag] = useState(true);

  useEffect(() => {
    // Force fresh fetch
    fetchPatientData(patientId);
  }, [patientId, fetchPatientData]);

  // Check feature flag when component mounts
  useEffect(() => {
    const checkFeatureFlag = async () => {
      if (!patientId) return;
      
      try {
        setIsLoadingFeatureFlag(true);
        const version = await checkReportLogicVersion(patientId);
        setReportLogicVersion(version);
      } catch (error) {
        console.error('Error checking feature flag:', error);
        setReportLogicVersion(1); // Default to old logic
      } finally {
        setIsLoadingFeatureFlag(false);
      }
    };

    checkFeatureFlag();
  }, [patientId]);

  // Strength titles live in the SSI section (section 2), not in the PR
  // section (section 6) that this page normally reads from. We pull them
  // separately so the legacyDocStructure can show the actual names.
  const ssiOutputs = useMemo(() =>
    patientDataModel?.dataModel?.['6_manageReports']?.sections?.['2']?.outputs || {},
    [patientDataModel]);

  // Legacy document structure (version 1) - includes all paragraphs for review
  const legacyDocStructure = useMemo(() => ({
    "Your Story": {
      "1": { "title": "Your Current Concerns", "code": "pl_presentingComplaintSummary" },
      "2": { "title": "Your Mental and Emotional Wellbeing", "code": "pl_psychiatricHistorySummary" },
      "3": { "title": "Your Physical Health", "code": "pl_medicalHistorySummary" },
      "4": { "title": "Your Family", "code": "pl_familyHistorySummary" },
      "5": { "title": "Your Childhood Experiences", "code": "pl_developmentalSocialHistoryChildhoodSummary" },
      "6": { "title": "Your Life Today", "code": "pl_developmentalSocialHistoryAdultSummary" },
      "7": { "title": "Your Lifestyle", "code": "pl_lifestyleHistorySummary" },
      "8": { "title": "Your Experiences with the Law", "code": "pl_forensicHistorySummary" },
      "9": { "title": "Our Observations", "code": "pl_mseSummary" },
    },
    "Your ADHD Profile": {
      "1": { "title": stripStrengthPrefix(ssiOutputs['pl_strength_1']) || "Strength 1", "code": "pl_strength_1_summary" },
      "2": { "title": stripStrengthPrefix(ssiOutputs['pl_strength_2']) || "Strength 2", "code": "pl_strength_2_summary" },
      "3": { "title": stripStrengthPrefix(ssiOutputs['pl_strength_3']) || "Strength 3", "code": "pl_strength_3_summary" },
      "6": { "title": "Work / Education Impairment", "code": "pl_workEducationImpairmentSummary" },
      "7": { "title": "Relationships and Social Impairment", "code": "pl_socialImpairmentSummary" },
      "8": { "title": "Psychological and Emotional Wellbeing Impairment", "code": "pl_psychologicalImpairmentSummary" },
    },
    "Your Diagnosis": {
      "1": { "title": "Decision for Provisional Primary Diagnosis", "code": "pl_notADHD_ppdSentence" },
      "2": { "title": "Confirmed Secondary and/or Comorbid Diagnoses", "code": "pl_confirmedSecondaryComorbidDiagnosesSentence" },
      "3": { "title": "Unconfirmed Secondary and/or Comorbid Diagnoses", "code": "pl_unconfirmedSecondaryComorbidDiagnosesSentence" },
    },
    "Your Next Steps": {
      "1": { "title": "Recommended Next Steps", "code": "pl_yourNextStepsSentence" },
    },
    "Your Goals": {
      "1": { "title": "Your Goals", "code": "pl_patientGoalsSummary" },
    },
    "Your Strategies and Recommendations": {
      // "1": { "title": "Disclosure with Family and Friends", "code": "pl_disclosureFamily" },
      // "2": { "title": "Disclosure at Work", "code": "pl_disclosureEmployer" },
      // "3": { "title": "Disclosure in Education", "code": "pl_disclosureEducation" },    

      "1": { "title": "Driving", "code": "pl_driving" },
      "2": { "title": "Physical Activity", "code": "pl_lifestylePhysicalActivity" },
      "3": { "title": "Diet", "code": "pl_lifestyleDiet" },
      "4": { "title": "Sleep", "code": "pl_lifestyleSleep" },
      "5": { "title": "Alcohol", "code": "pl_lifestyleAlcohol" },
      "6": { "title": "Smoking & Tobacco Use", "code": "pl_lifestyleSmoking" },
      "7": { "title": "Caffeine", "code": "pl_lifestyleCaffeine" },
      "8": { "title": "Gambling & Gaming Behaviour", "code": "pl_lifestyleGambling" },
      "9": { "title": "Substance Use", "code": "pl_lifestyleSubstanceUse" },
      "10": { "title": "Financial Management", "code": "pl_lifestyleFinances" },

      "11": { "title": "Environmental Modifications at Home", "code": "pl_environmentHome" },
      "12": { "title": "Environmental Modifications at Work", "code": "pl_environmentWork" },
      "13": { "title": "Environmental Modifications in Education", "code": "pl_environmentEducation" },
    },
    "Your Treatment Options": {
      "1": { "title": "Your Treatment Preferences", "code": "pl_treatmentPreferencesSentence" },
    },
    "Your Agreed Plan": {
      "1": { "title": "Health Checks - Weight", "code": "pl_healthChecksWeight" },
      "2": { "title": "Health Checks - Blood Pressure", "code": "pl_healthChecksBloodPressure" },
      "3": { "title": "Health Checks - Pulse", "code": "pl_healthChecksPulse" },
      "4": { "title": "Health Checks - Medical History Review", "code": "pl_healthChecksMedicalHistory" },
      "5": { "title": "Outcome of Health Checks", "code": "pl_outcomeOfHealthChecksSentence" },
      "6": { "title": "Your Medication Plan", "code": "pl_medicationPlanSentence" },
      "7": { "title": "Instructions for Week 1", "code": "pl_medicationPlanWeek1" },
      "8": { "title": "Instructions for Week 2", "code": "pl_medicationPlanWeek2" },
      "9": { "title": "Instructions for Week 3", "code": "pl_medicationPlanWeek3" },
      "10": { "title": "Instructions for Week 4", "code": "pl_medicationPlanWeek4" },
    },
    "Appendices: Your ADHD Traits": {
      "1": { "title": "Attention to Detail", "code": "pl_ssi_symptom_1_sentence" },
      "2": { "title": "Keeping Attention", "code": "pl_ssi_symptom_2_sentence" },
      "3": { "title": "Listening", "code": "pl_ssi_symptom_3_sentence" },
      "4": { "title": "Following Instructions", "code": "pl_ssi_symptom_4_sentence" },
      "5": { "title": "Organisation", "code": "pl_ssi_symptom_5_sentence" },
      "6": { "title": "Engagement in Tasks", "code": "pl_ssi_symptom_6_sentence" },
      "7": { "title": "Losing Items", "code": "pl_ssi_symptom_7_sentence" },
      "8": { "title": "Distractions", "code": "pl_ssi_symptom_8_sentence" },
      "9": { "title": "Forgetfulness", "code": "pl_ssi_symptom_9_sentence" },
      "10": { "title": "Fidgeting", "code": "pl_ssi_symptom_10_sentence" },
      "11": { "title": "Remaining Seated", "code": "pl_ssi_symptom_11_sentence" },
      "12": { "title": "Restlessness", "code": "pl_ssi_symptom_12_sentence" },
      "13": { "title": "Engaging in Quiet Activities", "code": "pl_ssi_symptom_13_sentence" },
      "14": { "title": "Always “On the Go”", "code": "pl_ssi_symptom_14_sentence" },
      "15": { "title": "Talking A Lot", "code": "pl_ssi_symptom_15_sentence" },
      "16": { "title": "Impulsive Communication", "code": "pl_ssi_symptom_16_sentence" },
      "17": { "title": "Difficulty Waiting Your Turn", "code": "pl_ssi_symptom_17_sentence" },
      "18": { "title": "Interrupting Others", "code": "pl_ssi_symptom_18_sentence" },
    },
  }), [ssiOutputs]);

  // Unified document structure (version 2) - excludes CI, SSI, and PAD paragraphs that are automatically reused
  const unifiedDocStructure = useMemo(() => ({
    // Note: "Your Story" section is excluded for version 2 patients as these paragraphs are automatically reused from CI
    // Note: "Your ADHD Profile" section, including strengths and impairment summaries, is excluded for version 2 patients as these paragraphs are generated after the SSI appointment   
    // Note: "Your Diagnosis" section, including decisions for PPD and SCD, is excluded for version 2 patients as these paragraphs are generated after the DD module       
    // Note: "Your Goals" section is excluded for version 2 patients as these paragraphs are automatically reused from PAD
    // Note: "Your Strategies and Recommendations" section is excluded for version 2 patients as these paragraphs are generated after the PAD appointment     
    // Note: "Your Treatment Options" section is excluded for version 2 patients as these paragraphs are automatically reused from PAD
    // Note: "Your Agreed Plan" section is excluded for version 2 patients as these paragraphs are now reviewed during PAD
    // Note: "Appendices: Your ADHD Traits" section is excluded as these are automatically reused from unified SSI symptom paragraphs
  }), []);

  // Choose the appropriate structure based on feature flag
  const docStructure = useMemo(() => {
    if (isLoadingFeatureFlag) {
      return legacyDocStructure; // Default to legacy while loading
    }
    return reportLogicVersion === 2 ? unifiedDocStructure : legacyDocStructure;
  }, [reportLogicVersion, isLoadingFeatureFlag, legacyDocStructure, unifiedDocStructure]);

  const REPORT_DATA_OUTPUTS = useMemo(() =>
    patientDataModel?.dataModel?.['6_manageReports']?.sections?.[`${MODULE_NUMBER}`]?.outputs || {},
    [patientDataModel]);

  // The paragraph edit history, read from the SAME already-loaded section as the
  // outputs above. It is what lets the adoption numbers (how much of the AI draft
  // survived) be computed at the moment of an edit rather than by reading the
  // report back out of Firestore later - and because this object is already in
  // memory to render the page, it costs no additional read.
  //
  // In-memory means it can lag a write made elsewhere in the same instant. That
  // only matters for a paragraph's FIRST edit, and only if another session edited
  // it seconds earlier; once the earliest entry exists it stays the earliest
  // forever. The nightly reconciliation reads authoritative Firestore, so a
  // disagreement surfaces there rather than going unnoticed.
  const REPORT_EDIT_HISTORY = useMemo(() =>
    patientDataModel?.dataModel?.['6_manageReports']?.sections?.[`${MODULE_NUMBER}`]?.editHistory || {},
    [patientDataModel]);

  const [state, setState] = useState({
    selectedCode: null,
    textareaContent: null,
    isSidePanelOpen: false,
    modelInputsFirestore: null,
    clinicianInputs: null,
    clinicianDecisions: null,
    checkedExamples: null,
    patientInputs: null,
    informantInputs: null
  });

  const [isEditing, setIsEditing] = useState(false);

  const {
    totalParagraphs, reviewedCodes, isFullyApproved,
    showWarning, addReviewedCode, removeReviewedCode,
  } = useApprovalTracking({
    patientId,
    moduleNumber: MODULE_NUMBER,
    docStructure,
    reportDataOutputs: REPORT_DATA_OUTPUTS,
  });

  const handleParagraphClick = (event, code, content) => {
    // Reset edit states
    setIsEditing(false);

    if (!state.modelInputsFirestore) {
      console.error('modelInputsFirestore is not populated');
      return;
    }

    if (code && state.modelInputsFirestore[code]) {
      setState(prevState => ({
        ...prevState,
        selectedCode: code,
        textareaContent: content || ' ', // always having at least a non-breaking space
        article: state.modelInputsFirestore[code]["Article"],
        clinicianInputs: state.modelInputsFirestore[code]["Clinician Notes"],
        clinicianDecisions: state.modelInputsFirestore[code]["Clinician Decisions"],
        patientInputs: state.modelInputsFirestore[code]["Patient Prefill"],
        // informantInputs: state.modelInputsFirestore[code]["Informant Prefill"],
        isSidePanelOpen: true
      }));
    } else {
      console.error('No matching input found for the code:', code);
    }
  };

  // Wrap shared renderModelInput with articleLinks enabled for this page's
  // ThinkDivergent evidence-based article sources (rendered as clickable links)
  const renderModelInput = (role, inputs) => _renderModelInput(role, inputs, { articleLinks: true });

  // Update the Section component to only render paragraphs with content
  const Section = ({ title, paragraphs }) => {
    // First filter out paragraphs that don't have content in REPORT_DATA_OUTPUTS
    const availableParagraphs = Object.entries(paragraphs).filter(
      ([_, { code }]) => REPORT_DATA_OUTPUTS[code] !== undefined && REPORT_DATA_OUTPUTS[code] !== ""
    );

    // Only render the section if it has at least one available paragraph
    if (availableParagraphs.length === 0) return null;

    return (
      <div className="mb-8">
        <h2 className="text-2xl font-bold mb-4 text-blue-500">{title}</h2>

        {availableParagraphs.map(([key, { title: subTitle, code }]) => {
          const content = REPORT_DATA_OUTPUTS[code];
          const decodedContent = decodeHtmlEntities(content); // Decode for display        
          return (
            <div key={key} className="mb-4">
              {subTitle && (
                <h3 className="font-semibold mb-2 text-gray-900">
                  {subTitle}
                </h3>
              )}
              <div
                data-paragraph-container
                className={`
                relative text-sm font-medium p-2 rounded-lg
                transition-all duration-200
                ${isEditing && state.selectedCode === code
                    ? 'bg-amber-100 z-20' // Edit mode background
                    : reviewedCodes.has(code)
                      ? 'bg-green-600/30 hover:bg-green-600/40 approved-paragraph'
                      : 'bg-violet-100 hover:bg-violet-200'
                  }
                ${state.selectedCode === code
                    ? 'ring ring-violet-800'
                    : ''
                  }
                ${!isEditing ? 'cursor-pointer' : ''}
              `}
                id={code}
                onClick={(e) => {
                  if (!isEditing) {
                    handleParagraphClick(e, code, decodedContent);
                    setTimeout(() => safeUpdatePosition(code), 0);
                  }
                }}
              >
                <p
                  ref={editableContentRef}
                  className="text-gray-700 leading-relaxed whitespace-pre-wrap"
                  contentEditable={isEditing && state.selectedCode === code}
                  onKeyDown={suppressFormattingShortcuts}
                  onInput={(e) => {
                    if (isEditing && state.selectedCode === code) {
                      let newContent = e.target.innerText;
                      newContent = newContent.replace(/\n/g, '\n');

                      if (REPORT_DATA_OUTPUTS) {
                        REPORT_DATA_OUTPUTS[code] = newContent;
                      }
                      safeUpdatePosition(code);
                    }
                  }}
                  onPaste={handlePlainTextPaste}
                  suppressContentEditableWarning={true}
                >
                  {decodedContent}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // Add a ref for the paragraph content
  const editableContentRef = useRef(null);

  // Update handleStartEdit to focus the content
  const handleStartEdit = () => {
    if (state.selectedCode) {
      // Store the current content before starting edit
      setPreEditContent(prev => ({
        ...prev,
        [state.selectedCode]: REPORT_DATA_OUTPUTS[state.selectedCode] || ''
      }));
    }
    setIsEditing(true);
    // Use setTimeout to ensure the contentEditable is enabled before focusing
    setTimeout(() => {
      const paragraphElement = document.querySelector(`#${state.selectedCode} p`);
      if (paragraphElement) {
        paragraphElement.focus();
        // Create a range and selection to place cursor at the end
        const range = document.createRange();
        const selection = window.getSelection();
        range.selectNodeContents(paragraphElement);
        range.collapse(false); // false means collapse to end
        selection.removeAllRanges();
        selection.addRange(range);
      }
      safeUpdatePosition(state.selectedCode);
    }, 0);
  };

  const handleSaveEdit = () => {
    if (state.selectedCode) {
      // Get the current and previous values
      const spanCode = state.selectedCode;
      const newValue = REPORT_DATA_OUTPUTS[spanCode];
      const previousValue = initialContent[spanCode] || '';

      // Only proceed if content actually changed
      if (newValue !== previousValue) {
        // Optimistically update UI first
        removeReviewedCode(state.selectedCode);
        setIsEditing(false);


        // Create history entry
        const editEntry = {
          clinicianId: auth.currentUser.uid,
          clinicianName: auth.currentUser.displayName,
          timestamp: serverTimestamp(),
          previousValue,
          newValue,
          editType: "manual",
        };
        const timestampKey = Date.now().toString();

        // Perform Firestore update in the background
        runTransaction(db, async (transaction) => {
          const docRef = doc(db, 'patients', patientId);
          const docSnap = await transaction.get(docRef);

          if (!docSnap.exists()) {
            throw new Error("Document does not exist!");
          }

          const data = docSnap.data();

          // Get current approved paragraphs and edit history
          const currentApprovedParagraphs = data.dataModel?.['6_manageReports']?.sections?.[`${MODULE_NUMBER}`]?.approvedParagraphs || {};
          const currentEditHistory = data.dataModel?.['6_manageReports']?.sections?.[`${MODULE_NUMBER}`]?.editHistory || {};

          // Remove from approved paragraphs
          delete currentApprovedParagraphs[state.selectedCode];

          // Add to edit history
          if (!currentEditHistory[spanCode]) {
            currentEditHistory[spanCode] = {};
          }
          currentEditHistory[spanCode][timestampKey] = editEntry;

          // Update the document atomically
          transaction.update(docRef, {
            // Update approved paragraphs
            [`dataModel.6_manageReports.sections.${MODULE_NUMBER}.approvedParagraphs`]: currentApprovedParagraphs,
            // Update edit history
            [`dataModel.6_manageReports.sections.${MODULE_NUMBER}.editHistory`]: currentEditHistory,
            // Reset the content to initial value
            [`dataModel.6_manageReports.sections.${MODULE_NUMBER}.outputs`]: {
              ...data.dataModel?.['6_manageReports']?.sections?.[MODULE_NUMBER]?.outputs,
              [spanCode]: newValue
            },
            // Update metadata
            [`dataModel.6_manageReports.sections.${MODULE_NUMBER}.lastReviewedAt`]: serverTimestamp(),
            [`dataModel.6_manageReports.sections.${MODULE_NUMBER}.reviewedBy`]: auth.currentUser.uid,
            [`dataModel.6_manageReports.sections.${MODULE_NUMBER}.reviewedByName`]: auth.currentUser.displayName
          });
        }).then(() => {
          // Analytics mirror of the edit that just persisted: paragraph code,
          // section and the two lengths only — never the wording itself.
          emitParagraphEdited({
            patientId,
            module: MODULE_PREFIX_CODE,
            docStructure,
            code: spanCode,
            prevText: previousValue,
            priorHistory: REPORT_EDIT_HISTORY[spanCode] || {},
            nextText: newValue,
          });
        }).catch(error => {
          console.error("Error updating paragraph approval on edit:", error);
          // Roll back optimistic update on error
          addReviewedCode(state.selectedCode);
          // Could show an error toast here
        });
      } else {
        // If no changes, just exit edit mode
        setIsEditing(false);
      }
    }
  };

  // Update handleCancel to revert to pre-edit content
  const handleCancel = () => {
    if (state.selectedCode && preEditContent[state.selectedCode]) {
      // Revert to the content before this edit session started
      if (REPORT_DATA_OUTPUTS) {
        REPORT_DATA_OUTPUTS[state.selectedCode] = preEditContent[state.selectedCode];
      }
    }
    setIsEditing(false);
    safeUpdatePosition(state.selectedCode);
  };

  const handleReset = () => {
    if (state.selectedCode && initialContent[state.selectedCode]) {
      const spanCode = state.selectedCode;
      const newValue = initialContent[state.selectedCode];
      const previousValue = REPORT_DATA_OUTPUTS[state.selectedCode];

      // Optimistically update UI
      if (REPORT_DATA_OUTPUTS) {
        REPORT_DATA_OUTPUTS[state.selectedCode] = initialContent[state.selectedCode];
      }

      // Remove from reviewed codes optimistically
      removeReviewedCode(state.selectedCode);

      // Create history entry for the reset
      const editEntry = {
        clinicianId: auth.currentUser.uid,
        clinicianName: auth.currentUser.displayName,
        timestamp: serverTimestamp(),
        previousValue,
        newValue,
        isReset: true  // Flag to indicate this was a reset action
      };
      const timestampKey = Date.now().toString();

      // Perform Firestore update
      runTransaction(db, async (transaction) => {
        const docRef = doc(db, 'patients', patientId);
        const docSnap = await transaction.get(docRef);

        if (!docSnap.exists()) {
          throw new Error("Document does not exist!");
        }

        const data = docSnap.data();

        // Get current approved paragraphs and edit history
        const currentApprovedParagraphs = data.dataModel?.['6_manageReports']?.sections?.[`${MODULE_NUMBER}`]?.approvedParagraphs || {};
        const currentEditHistory = data.dataModel?.['6_manageReports']?.sections?.[`${MODULE_NUMBER}`]?.editHistory || {};

        // Remove from approved paragraphs
        delete currentApprovedParagraphs[spanCode];

        // Add to edit history
        if (!currentEditHistory[spanCode]) {
          currentEditHistory[spanCode] = {};
        }
        currentEditHistory[spanCode][timestampKey] = editEntry;

        // Update the document atomically
        transaction.update(docRef, {
          // Update approved paragraphs
          [`dataModel.6_manageReports.sections.${MODULE_NUMBER}.approvedParagraphs`]: currentApprovedParagraphs,
          // Update edit history
          [`dataModel.6_manageReports.sections.${MODULE_NUMBER}.editHistory`]: currentEditHistory,
          // Reset the content to initial value
          [`dataModel.6_manageReports.sections.${MODULE_NUMBER}.outputs`]: {
            ...data.dataModel?.['6_manageReports']?.sections?.[MODULE_NUMBER]?.outputs,
            [spanCode]: newValue
          },
          // Update metadata
          [`dataModel.6_manageReports.sections.${MODULE_NUMBER}.lastReviewedAt`]: serverTimestamp(),
          [`dataModel.6_manageReports.sections.${MODULE_NUMBER}.reviewedBy`]: auth.currentUser.uid,
          [`dataModel.6_manageReports.sections.${MODULE_NUMBER}.reviewedByName`]: auth.currentUser.displayName
        });
      }).then(() => {
        // Analytics mirror of the reset that just persisted (code only).
        emitParagraphReset({
          patientId,
          module: MODULE_PREFIX_CODE,
          docStructure,
          code: spanCode,
        });
      }).catch(error => {
        console.error("Error resetting paragraph:", error);
        // Roll back optimistic updates on error
        if (REPORT_DATA_OUTPUTS) {
          REPORT_DATA_OUTPUTS[state.selectedCode] = previousValue;
        }
        addReviewedCode(state.selectedCode);
        // Could show an error toast here
      });

      setIsEditing(false);
    }
  };

  // Helper function to check if content has been modified from original
  const hasBeenModified = (code) => {
    if (!code) return false;
    return REPORT_DATA_OUTPUTS[code] !== initialContent[code];
  };

  const [showInitialWarning, setShowInitialWarning] = useState(true);
  // showWarning is provided by useApprovalTracking

  // State for initial content (on page load)
  const [initialContent, setInitialContent] = useState({});

  // Store initial content on page load, only for paragraphs with content
  useEffect(() => {
    if (REPORT_DATA_OUTPUTS) {
      const initialValues = {};
      // Loop through docStructure to get all codes
      Object.values(docStructure).forEach(section => {
        Object.values(section).forEach(({ code }) => {
          // Only store content that exists
          if (code && REPORT_DATA_OUTPUTS[code] !== undefined && REPORT_DATA_OUTPUTS[code] !== "") {
            initialValues[code] = REPORT_DATA_OUTPUTS[code];
          }
        });
      });
      setInitialContent(initialValues);
    }
  }, [REPORT_DATA_OUTPUTS, docStructure]);

  // State for pre-edit content (before current edit session)
  const [preEditContent, setPreEditContent] = useState({});

  const documentContainerRef = useRef(null);
  const { panelRef, panelPosition, safeUpdatePosition } = useFloatingActionPanel({
    containerRef: documentContainerRef,
    selectedCode: state.selectedCode,
    isEditing,
    topOffset: -15,
  });

  const [isSaving, setIsSaving] = useState(false);

  // Report type is determined by the canonical function in reportTypeUtils.js
  // which is workflow-config-aware (handles flat vs hierarchical DF models and
  // always-SSI orgs correctly).
  const determineReportType = useCallback(
    (patientData) => determinePatientReportType(patientData, claims?.organisation),
    [claims?.organisation]
  );

  const handleExitReview = async () => {
    setIsSaving(true);

    try {
      // Create the update object based on approval status
      const manageReports = patientDataModel?.dataModel?.['6_manageReports'];
      const updates = {
        [`dataModel.6_manageReports.${MODULE_PREFIX_CODE}ReportApproved`]: isFullyApproved,
        [`dataModel.6_manageReports.${MODULE_PREFIX_CODE}ReportApprovedBy`]: isFullyApproved ? auth.currentUser.uid : null,
        [`dataModel.6_manageReports.${MODULE_PREFIX_CODE}ReportApprovedByName`]: isFullyApproved ? auth.currentUser.displayName : null,
        [`dataModel.6_manageReports.${MODULE_PREFIX_CODE}ReportApprovedAt`]: isFullyApproved ? serverTimestamp() : null,
        ...(isFullyApproved ? buildFirstApprovedFieldsDotNotation(MODULE_PREFIX_CODE, manageReports) : {}),
      };

      // Run a single transaction to update the document
      await runTransaction(db, async (transaction) => {
        const docRef = doc(db, 'patients', patientId);
        const docSnap = await transaction.get(docRef);

        if (!docSnap.exists()) {
          throw new Error("Document does not exist!");
        }

        // Update the document atomically
        transaction.update(docRef, updates);
      });

      appendReportAuditEntry(patientId, {
        module: MODULE_PREFIX_CODE,
        eventType: 'approved',
        isFirstOccurrence: isFullyApproved && !manageReports?.prReportFirstApprovedAt,
      });

      // Only trigger document generation if fully approved
      if (isFullyApproved) {
        // Determine the correct report type based on patient data
        const reportType = determineReportType(patientDataModel);
        console.log('Using report type:', reportType);

        try {
          // Fire and forget - don't wait for response
          TriggerRenderPatientReport({
            clinicianId: auth.currentUser.uid,
            patientId: patientId,
            reportType: reportType,
            cfName: getRenderCfName(patientDataModel),
          });
        } catch (error) {
          console.error('Failed to trigger document generation:', error);
          // Continue with navigation even if document generation fails
        }
      }

      // Clear any cached data before navigating
      clearPatientData();

      // Navigate back to documents page
      navigate(`/patient/${patientId}/documents`);
    } catch (error) {
      console.error('Error during document review completion:', error);
      // Could add error toast here
    } finally {
      setIsSaving(false);
    }
  };

  const goToFirstUnreviewed = () => {
    // Only get paragraphs that are actually rendered on the page
    const allParagraphs = document.querySelectorAll('[data-paragraph-container]');
    const firstUnreviewed = Array.from(allParagraphs).find(p => !reviewedCodes.has(p.id));

    if (firstUnreviewed && documentContainerRef.current) {
      const containerRect = documentContainerRef.current.getBoundingClientRect();
      const nextRect = firstUnreviewed.getBoundingClientRect();

      // Calculate scroll position to center the paragraph
      const scrollTop = documentContainerRef.current.scrollTop +
        (nextRect.top - containerRect.top) -
        containerRect.height / 3;

      // Scroll to position
      documentContainerRef.current.scrollTo({
        top: scrollTop,
        behavior: 'smooth'
      });

      // After scroll completes, trigger click handler
      setTimeout(() => {
        const nextCode = firstUnreviewed.id;
        const nextContent = REPORT_DATA_OUTPUTS[nextCode];

        // Trigger the full paragraph click handler
        handleParagraphClick(
          { currentTarget: firstUnreviewed },
          nextCode,
          nextContent
        );
      }, 300);
    }
  };

  useEffect(() => {
    // Apply the overflow: hidden style when the component mounts
    document.body.style.overflow = 'hidden';
    // Clean up the style when the component unmounts
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  // Add useEffect to fetch model inputs
  useEffect(() => {
    const fetchModelInputs = async () => {
      try {
        const inputs = patientDataModel?.dataModel?.['6_manageReports']?.sections?.[`${MODULE_NUMBER}`]?.inputs;
        if (inputs) {
          setState(prev => ({ ...prev, modelInputsFirestore: inputs }));
        } else {
          console.error('Inputs data is missing');
        }
      } catch (error) {
        console.error('Error fetching model inputs from Firestore:', error);
      }
    };

    if (patientDataModel) {
      fetchModelInputs();
    }
  }, [patientDataModel]);


  // Modify the overlay to explicitly allow scroll events
  useEffect(() => {
    // Store ref in a variable inside the effect
    const currentContainer = documentContainerRef.current;

    if (isEditing && currentContainer) {
      // Save original overflow setting
      const originalOverflow = currentContainer.style.overflow;

      // Ensure the document container is still scrollable during editing
      currentContainer.style.overflow = 'auto';

      // Create a wheel event handler that prevents propagation but allows default behavior
      const handleWheel = (e) => {
        e.stopPropagation();
        // But let the default scroll behavior happen
      };

      // Add the wheel event listener
      currentContainer.addEventListener('wheel', handleWheel, { passive: true });

      // Clean up function uses the stored variable
      return () => {
        currentContainer.removeEventListener('wheel', handleWheel);
        currentContainer.style.overflow = originalOverflow;
      };
    }
  }, [isEditing]);

  const handlePageClick = useCallback((event) => {
    // Ignore clicks if we're in edit mode
    if (isEditing) {
      return;
    }

    // Ignore clicks on paragraphs or the action panel
    if (
      event.target.closest('[data-paragraph-container]') ||
      event.target.closest('[data-action-panel]')
    ) {
      return;
    }
    setIsEditing(false);
    // Reset state
    setState(prev => ({
      ...prev,
      selectedCode: null,
      selectedContent: null,
      isSidePanelOpen: false
    }));
  }, [isEditing]);

  // Add useEffect to set up and clean up click handler
  useEffect(() => {
    // Store ref in a variable inside the effect
    const container = documentContainerRef.current;

    if (container) {
      container.addEventListener('click', handlePageClick);
    }

    return () => {
      // Use the stored variable in cleanup
      if (container) {
        container.removeEventListener('click', handlePageClick);
      }
    };
  }, [handlePageClick]);

  const handleParagraphApproval = async (code, isApproving) => {
    console.log(`Starting paragraph approval. Code: ${code}, Approving: ${isApproving}`);
    try {
      await runTransaction(db, async (transaction) => {
        const docRef = doc(db, 'patients', patientId);
        console.log('Getting document snapshot...');
        const docSnap = await transaction.get(docRef);

        if (!docSnap.exists()) {
          throw new Error("Document does not exist!");
        }

        const data = docSnap.data();
        console.log('Current document data:', data);

        const currentApprovedParagraphs = data.dataModel?.['6_manageReports']?.sections?.[`${MODULE_NUMBER}`]?.approvedParagraphs || {};
        console.log('Current approved paragraphs:', currentApprovedParagraphs);

        if (isApproving) {
          // Add to approved paragraphs
          const newApproval = {
            approvedAt: serverTimestamp(),
            approvedBy: auth.currentUser.uid,
            approvedByName: auth.currentUser.displayName,
            content: REPORT_DATA_OUTPUTS[code]
          };
          console.log('Adding new approval:', newApproval);
          currentApprovedParagraphs[code] = newApproval;
        } else {
          console.log('Removing approval for code:', code);
          delete currentApprovedParagraphs[code];
        }

        const updateData = {
          [`dataModel.6_manageReports.sections.${MODULE_NUMBER}.approvedParagraphs`]: currentApprovedParagraphs,
          [`dataModel.6_manageReports.sections.${MODULE_NUMBER}.lastReviewedAt`]: serverTimestamp(),
          [`dataModel.6_manageReports.sections.${MODULE_NUMBER}.reviewedBy`]: auth.currentUser.uid,
          [`dataModel.6_manageReports.sections.${MODULE_NUMBER}.reviewedByName`]: auth.currentUser.displayName
        };
        console.log('Updating document with:', updateData);

        // Update the document atomically
        transaction.update(docRef, updateData);
      });
      console.log('Transaction completed successfully');

      // Analytics mirror of the approval change that just persisted
      // (code only). Both directions share the review control that
      // wrote it, so both share this one call.
      emitParagraphApproval({
        patientId,
        module: MODULE_PREFIX_CODE,
        docStructure,
        code,
        assessmentType: 'adhd',
        isApproving,
      });
    } catch (error) {
      console.error("Error updating paragraph approval:", error);
      console.error("Error details:", {
        code: error.code,
        message: error.message,
        stack: error.stack
      });
    }
  };

  // Approved paragraphs are now loaded by useApprovalTracking

  const handleMarkAsDone = async () => {
    if (!state.selectedCode) {
      console.log('No code selected, cannot mark as done');
      return;
    }

    console.log('Starting handleMarkAsDone for code:', state.selectedCode);
    const paragraphElement = document.getElementById(state.selectedCode);
    if (paragraphElement) {
      try {
        console.log('Calling handleParagraphApproval...');
        handleParagraphApproval(state.selectedCode, true).catch(error => {
          console.error('Error in handleParagraphApproval:', error);
          // Roll back the optimistic update on error
          removeReviewedCode(state.selectedCode);
          // Could also show an error toast here
        });

        // Find next paragraph before updating UI state
        const allParagraphs = document.querySelectorAll('[data-paragraph-container]');
        const currentIndex = Array.from(allParagraphs).findIndex(p => p.id === state.selectedCode);
        const nextUnreviewed = Array.from(allParagraphs).find((p, index) =>
          index > currentIndex && !reviewedCodes.has(p.id)
        );

        // Update reviewed state
        addReviewedCode(state.selectedCode);

        if (nextUnreviewed && documentContainerRef.current) {
          const containerRect = documentContainerRef.current.getBoundingClientRect();
          const nextRect = nextUnreviewed.getBoundingClientRect();

          const scrollTop = documentContainerRef.current.scrollTop +
            (nextRect.top - containerRect.top) -
            containerRect.height / 3;

          // Then scroll
          documentContainerRef.current.scrollTo({
            top: scrollTop,
            behavior: 'smooth'
          });

          setTimeout(() => {
            const nextCode = nextUnreviewed.id;

            // Update state with new paragraph
            setState(prev => ({
              ...prev,
              selectedCode: nextCode,
              isSidePanelOpen: true,
              article: state.modelInputsFirestore[nextCode]["Article"],
              clinicianInputs: state.modelInputsFirestore[nextCode]["Clinician Notes"],
              clinicianDecisions: state.modelInputsFirestore[nextCode]["Clinician Decisions"],
              // checkedExamples: state.modelInputsFirestore[code]["Checked Examples"],
              patientInputs: state.modelInputsFirestore[nextCode]["Patient Prefill"],
              // informantInputs: state.modelInputsFirestore[nextCode]["Informant Prefill"]
            }));

            // Pass the new code directly to position update
            setTimeout(() => safeUpdatePosition(nextCode), 0);
          }, 300);
        } else {
          // This was the last paragraph - unselect it
          setState(prev => ({
            ...prev,
            selectedCode: null,
            isSidePanelOpen: false,
            clinicianInputs: null,
          }));
        }
      } catch (error) {
        console.error('Error in handleMarkAsDone:', error);
        console.error("Error details:", {
          code: error.code,
          message: error.message,
          stack: error.stack
        });
      }
    }
  };

  // Update handleSkip with the same scroll logic
  const handleSkip = () => {
    if (!state.selectedCode) return;

    const paragraphElement = document.getElementById(state.selectedCode);
    if (paragraphElement) {
      // Find next paragraph BEFORE updating UI state
      const allParagraphs = document.querySelectorAll('[data-paragraph-container]');
      const currentIndex = Array.from(allParagraphs).findIndex(p => p.id === state.selectedCode);
      const nextUnreviewed = Array.from(allParagraphs).find((p, index) =>
        index > currentIndex && !reviewedCodes.has(p.id)
      );

      if (nextUnreviewed && documentContainerRef.current) {
        const containerRect = documentContainerRef.current.getBoundingClientRect();
        const nextRect = nextUnreviewed.getBoundingClientRect();

        const scrollTop = documentContainerRef.current.scrollTop +
          (nextRect.top - containerRect.top) -
          containerRect.height / 3;

        // Then scroll
        documentContainerRef.current.scrollTo({
          top: scrollTop,
          behavior: 'smooth'
        });

        setTimeout(() => {
          const nextCode = nextUnreviewed.id;

          // Update state with new paragraph
          setState(prev => ({
            ...prev,
            selectedCode: nextCode,
            isSidePanelOpen: true,
            article: state.modelInputsFirestore[nextCode]["Article"],
            clinicianInputs: state.modelInputsFirestore[nextCode]["Clinician Notes"],
            clinicianDecisions: state.modelInputsFirestore[nextCode]["Clinician Decisions"],
            patientInputs: state.modelInputsFirestore[nextCode]["Patient Prefill"],
            // informantInputs: state.modelInputsFirestore[nextCode]["Informant Prefill"]
          }));

          // Pass the new code directly to position update
          setTimeout(() => safeUpdatePosition(nextCode), 0);
        }, 300);
      } else {
        // This was the last paragraph - unselect it
        setState(prev => ({
          ...prev,
          selectedCode: null,
          isSidePanelOpen: false,
          clinicianInputs: null,
        }));
      }
    }
  };

  // Add progress calculation
  const calculateProgress = useCallback(() => {
    if (totalParagraphs === 0) return 0;
    return Math.round((reviewedCodes.size / totalParagraphs) * 100);
  }, [reviewedCodes.size, totalParagraphs]);

  // Gate the entire interactive UI behind the feature flag. Without this,
  // the legacy doc structure is used during loading, which produces zero
  // matching paragraphs for v2 patients. That causes totalParagraphs === 0,
  // the header shows "Complete Review", and clicking it marks the report as
  // approved despite no paragraphs actually being reviewed.
  if (isLoadingFeatureFlag) {
    return (
      <Layout activePage="dashboard" scrollable={false}>
        <div className="h-full flex items-center justify-center">
          <div className="flex flex-col items-center gap-3">
            <svg className="animate-spin h-8 w-8 text-violet-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
            <p className="text-slate-600 font-medium">Loading document...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout activePage="dashboard" scrollable={false}>
      <InitialWarningDialog showInitialWarning={showInitialWarning} setShowInitialWarning={setShowInitialWarning} />
      <div className="h-full flex flex-col overflow-hidden  ">

        {/* // Add overlay component when editing */}
        {isEditing && (
          <div
            className="fixed inset-0 bg-gray-900/20 z-10 transition-opacity duration-200"
            style={{
              pointerEvents: 'none' // Allow clicks to pass through to underlying elements
            }}
          />
        )}

        {/* Fixed header section */}
        <div className="flex-none pt-4">
          <DocumentReviewHeader
            title={`Reviewing: ${MODULE_TITLE}`}
            reviewedCodes={reviewedCodes}
            totalParagraphs={totalParagraphs}
            isSaving={isSaving}
            handleExitReview={handleExitReview}
            goToFirstUnreviewed={goToFirstUnreviewed}
            calculateProgress={calculateProgress}
          />
          <WarningBar show={showWarning} />
        </div>

        {/* Scrollable document section */}
        <div className="flex-1 px-8 pb-8 min-h-0 relative">
          <div className="document-display-container h-full overflow-hidden bg-slate-800">
            {/* Main flex container for document and side panel */}
            <div className="flex h-full">
              {/* Left side - Document container */}
              <div
                ref={documentContainerRef}
                className={`
                  w-1/2 relative m-4 p-16 border shadow-md bg-white
                  ${isEditing ? 'overflow-hidden' : 'overflow-y-auto'}
                `}
              >
                <div className="space-y-8 text-left">
                  {Object.entries(docStructure).map(([title, paragraphs]) => (
                    <Section
                      key={title}
                      title={title}
                      paragraphs={paragraphs}
                    />
                  ))}
                </div>
                <div className="h-[calc(100vh-32rem)]" aria-hidden="true" />

                {/* Floating Action Panel */}
                <FloatingActionPanel
                  panelRef={panelRef}
                  position={panelPosition}
                  visible={Boolean(state.selectedCode)}
                  isEditing={isEditing}
                  hasBeenModified={hasBeenModified(state.selectedCode)}
                  onStartEdit={handleStartEdit}
                  onSave={handleSaveEdit}
                  onCancel={handleCancel}
                  onReset={handleReset}
                  onApprove={handleMarkAsDone}
                  onSkip={handleSkip}
                />
              </div>

              {/* Right side - Side Panel Container */}
              <div className={`
                  w-2/5 p-4 flex flex-col
                  ${!state.selectedCode ? 'hidden' : ''}
                `}>
                <div className={`
                    flex-1 text-sm bg-gray-100 rounded-xl overflow-y-auto 
                    border shadow-sm
                    flex flex-col h-full
                  `}>
                  {state.selectedCode && (
                    <div className="flex flex-col h-full p-6 overflow-hidden">
                      <div className="flex-1 flex flex-col min-h-0">
                        <div className="flex-none flex justify-between items-center">
                          <p className="text-xl font-bold text-gray-800 mb-2">AI Inputs</p>
                        </div>
                        <div className="overflow-y-auto">
                          {renderModelInput("Your decisions", state.clinicianDecisions)}
                          {renderModelInput("Your notes", state.clinicianInputs)}
                          {renderModelInput("Your checked examples", state.checkedExamples)}
                          {renderModelInput("Patient's report", state.patientInputs)}
                          {renderModelInput("Informant comments", state.informantInputs)}
                          {renderModelInput("ThinkDivergent evidence-based article", state.article)}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}